"use client";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays, MapPin, Clock, Menu, X,
  Plane, Users, Target, Coffee, Lightbulb, Sunset, Mail , Brain , Network , ZoomIn
} from "lucide-react";
import { Image } from "@imagekit/next";
import { OverlapGroupSection } from "../../../(Home)/(sections)";



const tagIconMap = {
  Founders: Users,
  Rishikesh: MapPin,
  "Mental Clarity": Brain,
  Networking: Network,
};
const iconMap = { Plane, Users, Target, Coffee, Lightbulb, Sunset };
const folder = process.env.NEXT_PUBLIC_IMAGEKIT_FOLDER || "";

  const slides = [
    "outsitting.JPG",
    "outinggroup.jpg",
    "closedsitting.webp",
    "guitaract.JPG",
  ];

/* ── Navbar ─────────────────────────────────────────── */
const Navbar = ({ brand, links }) => {
  const [open, setOpen] = useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-14">
        <a href="/" className="text-xl font-serif font-bold text-foreground tracking-tight">{brand}</a>
        <ul className="hidden sm:flex gap-8">
          {links.map((l) => (
            <li key={l}>
              <a href="#" className="text-sm font-sans text-muted-foreground hover:text-foreground transition-colors">{l}</a>
            </li>
          ))}
        </ul>
        <button onClick={() => setOpen(!open)} className="sm:hidden text-foreground" aria-label="Toggle menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="sm:hidden bg-background border-b border-border overflow-hidden"
          >
            <ul className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <li key={l}>
                  <a href="#" className="block py-2 text-sm font-sans text-muted-foreground hover:text-foreground transition-colors">{l}</a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

/* ── Hero Carousel ──────────────────────────────────── */
const HeroCarousel = ({ slides , title }) => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (!slides?.length) return;
    const timer = setInterval(next, 2000);
    return () => clearInterval(timer);
  }, [next, slides]);

  if (!slides?.length) return null;

  return (
    <section className="relative h-[70vh] sm:h-[85vh] overflow-hidden">
      <AnimatePresence>
        <motion.div
          key={current}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          <Image
            src={slides[current]}
            alt=""
            fill
            priority={current === 0}
            sizes="100vw"
            className="object-cover scale-105"
          />
        </motion.div>
      </AnimatePresence>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-20 px-6 text-center text-white">
        <motion.h1
          key={`title-${current}`}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl sm:text-6xl font-serif font-bold mb-4"
        >
          {title}
        </motion.h1>
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              i === current ? "bg-white scale-125" : "bg-white/40"
            }`}
          />
        ))}
      </div>
    </section>
  );
};
/* ── Meta Info ──────────────────────────────────────── */
const MetaInfo = ({ date, location, readTime }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="flex flex-wrap items-center gap-4 sm:gap-6 text-muted-foreground font-sans text-sm py-6 border-b border-border"
  >
    <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-primary" />{date}</span>
    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" />{location}</span>
    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" />{readTime}</span>
  </motion.div>
);

/* ── Intro ──────────────────────────────────────────── */
const IntroSection = ({ text }) => (
  <motion.section
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="py-10 sm:py-16"
  >
    <p className="text-lg sm:text-xl lg:text-2xl leading-relaxed text-foreground/90 font-sans first-letter:text-5xl first-letter:font-serif first-letter:font-bold first-letter:text-primary first-letter:float-left first-letter:mr-3 first-letter:mt-1">
      {text}
    </p>
  </motion.section>
);

/* ── Content Block ──────────────────────────────────── */
const ContentBlock = ({ title, text, image, imageAlt, reverse }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7 }}
    className={`flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-8 lg:gap-14 items-center py-10 sm:py-16`}
  >
    <div className="flex-1 space-y-4">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground">{title}</h2>
      <p className="text-base sm:text-lg leading-relaxed text-muted-foreground font-sans">{text}</p>
    </div>
    <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }} className="flex-1 w-full relative h-64 sm:h-80 lg:h-96">
      <Image 
        src={image} 
        alt={imageAlt} 
        fill
        className="object-cover rounded-lg shadow-lg" 
        loading="lazy" 
      />
    </motion.div>
  </motion.div>
);

const SectionsRenderer = ({ sections }) => {
  if (!sections?.length) return null;

  return (
    <section className="divide-y divide-border">
      {sections.map((section, index) => {

        return (
          <ContentBlock
            key={index}
            title={section.heading}
            text={section.content}
            image={section.image}
            imageAlt={section.heading}
            reverse={index % 2 !== 0}
          />
        );
      })}
    </section>
  );
};

/* ── Image Gallery ──────────────────────────────────── */
const ImageGallery = ({ images }) => {
  const [selected, setSelected] = useState(null);

  const getSpan = (i) => {
    const patterns = [
      "col-span-2 row-span-2", 
      "col-span-1 row-span-1", 
      "col-span-1 row-span-2", 
      "col-span-1 row-span-1", 
      "col-span-1 row-span-1", 
      "col-span-2 row-span-1", 
    ];
    return patterns[i % patterns.length];
  };

  return (
    <section className="py-14 sm:py-20">
      {/* Section Title */}
    <motion.h2
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground mb-10 text-center"
    >
      Moments Captured
    </motion.h2>

      {/* Grid */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3"
        style={{ gridAutoRows: "160px" }}
      >
        {images.map((img, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: i * 0.07, ease: [0.23, 1, 0.32, 1] }}
            className={`relative overflow-hidden rounded-lg cursor-pointer group bg-muted ${getSpan(i)}`}
            onClick={() => setSelected(img)}
          >
            {/* Image */}
            <Image
              src={img}
              alt={`Gallery image ${i + 1}`}
              fill
              sizes="(max-width:768px) 50vw, (max-width:1200px) 25vw, 400px"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />

            {/* Hover dark overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-500 ease-out" />

            {/* Zoom icon — appears on hover */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-full p-2.5">
                <ZoomIn className="w-5 h-5 text-white" />
              </div>
            </div>

            {/* Bottom index label — slides up on hover */}
            <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out">
              <span className="text-white text-xs font-sans tracking-widest uppercase">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4 sm:p-10"
            onClick={() => setSelected(null)}
          >
            {/* Close button */}
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Lightbox image wrapper */}
            <motion.div
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.88, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
              className="relative w-full max-w-4xl rounded-xl overflow-hidden shadow-2xl"
              style={{ aspectRatio: "16/9", maxHeight: "85vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <Image
                src={selected}
                alt="Full view"
                fill
                sizes="90vw"
                className="object-contain"
                priority
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};


const Timeline = ({ tags }) => {
  return (
    <section className="py-14 sm:py-20">
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold mb-12 text-center"
      >
        What Emerged From The Retreat
      </motion.h2>

      <div className="relative max-w-4xl mx-auto">

        {/* CENTER VERTICAL LINE */}
        <motion.div
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="origin-top absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[2px] bg-yellow-400"
        />

        {tags.map((tag, i) => {
          const Icon = tagIconMap[tag] || Sparkles;

          return (
            <motion.div
              key={tag}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="relative flex items-center mb-12"
            >
              {/* ICON ON CENTER LINE */}
              <div
                className="absolute left-1/2 -translate-x-1/2
                           flex items-center justify-center
                           w-20 h-20 rounded-full
                           bg-card border-2 border-[#FFAE00] shadow-md z-10"
              >
                <Icon className="w-10 h-10 text-black" />
              </div>

              {/* TAG CARD — LEFT SIDE */}
              <div className="w-1/2 pr-10 text-right">
                <div className="inline-block bg-card border shadow-md rounded-xl px-5 py-3">
                  <p className="font-semibold">{tag}</p>
                </div>
              </div>

              {/* EMPTY RIGHT SIDE FOR BALANCE */}
              <div className="w-1/2" />
            </motion.div>
          );
        })}
      </div>
    </section>
    
  );
};

/* ── Conclusion ─────────────────────────────────────── */
const Conclusion = ({ title, text }) => (
  <motion.section
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="py-14 sm:py-20 text-center"
  >
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground mb-6">{title}</h2>
    <p className="text-base sm:text-lg leading-relaxed text-muted-foreground font-sans max-w-2xl mx-auto">{text}</p>
  </motion.section>
);

/* ── CTA & Related ──────────────────────────────────── */
const CTARelated = ({ cta, related }) => (
  <section className="py-14 sm:py-20 space-y-16">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-card rounded-2xl p-8 sm:p-12 text-center shadow-lg border border-border"
    >
      <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-3">{cta.title}</h2>
      <p className="text-muted-foreground font-sans mb-6">{cta.subtitle}</p>
      <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <div className="flex-1 flex items-center gap-2 bg-background border border-border rounded-lg px-4 py-2.5">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <input type="email" placeholder="your@email.com" className="bg-transparent flex-1 text-sm font-sans outline-none text-foreground placeholder:text-muted-foreground" />
        </div>
        <button className="bg-primary text-primary-foreground font-sans text-sm font-medium px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity">
          Subscribe
        </button>
      </div>
    </motion.div>
    <div>
      <h3 className="text-xl sm:text-2xl font-serif font-bold text-foreground mb-6 text-center">More Stories</h3>
      <div className="grid sm:grid-cols-3 gap-6">
        {related.map((r, i) => (
          <motion.a
            key={i}
            href="#"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            whileHover={{ y: -4 }}
            className="group block bg-card rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <span className="text-xs font-sans text-primary font-semibold">{r.date}</span>
              <h4 className="text-base font-serif font-semibold text-foreground mt-1 group-hover:text-primary transition-colors">{r.title}</h4>
              <p className="text-sm text-muted-foreground font-sans mt-2">{r.excerpt}</p>
            </div>
          </motion.a>
        ))}
      </div>
    </div>
  </section>
);

/* ── Footer ─────────────────────────────────────────── */
const Footer = ({ copyright, links }) => (
  <footer className="border-t border-border py-10 mt-10">
    <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm font-sans text-muted-foreground">
      <span>{copyright}</span>
      <div className="flex gap-6">
        {links.map((l) => (
          <a key={l} href="#" className="hover:text-foreground transition-colors">{l}</a>
        ))}
      </div>
    </div>
  </footer>
);

/* ── Main Page ──────────────────────────────────────── */
export default function BlogPost({ post,data }) {

  if (!data) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <main>
        <HeroCarousel slides={data.images} title={data.title} />
        <article className="max-w-4xl mx-auto px-6 sm:px-8">
          <MetaInfo {...data.meta} />
          <IntroSection text={data.intro} />
          <section className="divide-y divide-border">
            <SectionsRenderer sections={data.sections} />
          </section>
        </article>
       <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <ImageGallery images={data.images} />
        </div>
        <div className="">
          <OverlapGroupSection/></div>

          {/* <article className="max-w-4xl mx-auto px-6 sm:px-8">
          <Conclusion {...data.conclusion} />
        </article>
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <CTARelated cta={data.cta} related={data.related} />
        </div> */}
      </main>
    </div>
  );
};

